<template>
  <q-dialog :model-value="modelValue" @update:model-value="close" persistent>
    <q-card class="promo-card" dir="rtl">
      <div class="promo-shimmer"></div>

      <q-btn icon="close" flat round dense class="promo-close" @click="close" />

      <q-card-section class="text-center q-pt-xl q-pb-sm">
        <div class="promo-badge">
          <q-icon name="local_fire_department" size="22px" />
          <span>عرض خاص</span>
        </div>
        <div class="promo-title">{{ promo.title }}</div>
        <div v-if="promo.body" class="promo-body">{{ promo.body }}</div>
      </q-card-section>

      <q-card-section v-if="promo.features && promo.features.length" class="q-px-lg q-py-sm">
        <div v-for="(f, i) in promo.features" :key="i" class="promo-feature">
          <q-icon name="check_circle" size="20px" />
          <span>{{ f }}</span>
        </div>
      </q-card-section>

      <q-card-section v-if="countdown" class="text-center q-py-sm">
        <div class="promo-countdown">
          <q-icon name="schedule" size="16px" class="q-ml-xs" />
          ينتهي العرض خلال: <b>{{ countdown }}</b>
        </div>
      </q-card-section>

      <q-card-actions align="center" class="q-pb-lg q-pt-sm">
        <q-btn unelevated rounded class="promo-cta" icon-right="arrow_back" label="اشحن الآن" @click="goRecharge" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { auth } from '../../config/firebase';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  promo: { type: Object, required: true }
});
const emit = defineEmits(['update:modelValue', 'dismissed']);

const router = useRouter();

// Live countdown to the end of endDate (inclusive last day => +1 day at 00:00 UTC).
const now = ref(Date.now());
let timer = null;

const endTs = computed(() => {
  const d = props.promo?.endDate;
  if (!d) return null;
  const t = new Date(`${String(d).slice(0, 10)}T00:00:00Z`).getTime();
  return isNaN(t) ? null : t + 24 * 3600 * 1000;
});

const countdown = computed(() => {
  if (!endTs.value) return '';
  const diff = endTs.value - now.value;
  if (diff <= 0) return 'انتهى العرض';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (days > 0) return `${days} ${days === 1 ? 'يوم' : days === 2 ? 'يومان' : 'أيام'} و ${hours} ساعة`;
  if (hours > 0) return `${hours} ساعة و ${mins} دقيقة`;
  return `${mins} دقيقة`;
});

onMounted(() => { timer = setInterval(() => { now.value = Date.now(); }, 30000); });
onBeforeUnmount(() => { if (timer) clearInterval(timer); });

const close = () => {
  emit('update:modelValue', false);
  emit('dismissed');
};

const goRecharge = () => {
  close();
  // Logged-in users go straight to the recharge page; visitors must register
  // first (they are then funnelled to recharge after onboarding).
  if (auth.currentUser) {
    router.push({ name: 'RechargeCredits' });
  } else {
    router.push({ name: 'Register' });
  }
};
</script>

<style scoped>
.promo-card {
  position: relative;
  width: 440px;
  max-width: 92vw;
  border-radius: 22px;
  overflow: hidden;
  color: #FFF8E7;
  background: linear-gradient(135deg, #1a1305 0%, #3d2802 45%, #5a3a00 100%);
  border: 1px solid rgba(255, 196, 0, 0.45);
  box-shadow: 0 0 42px rgba(255, 170, 0, 0.28), 0 24px 60px rgba(0, 0, 0, 0.6);
}

/* Animated golden shimmer sweeping across the card */
.promo-shimmer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(115deg, transparent 30%, rgba(255, 220, 120, 0.16) 48%, rgba(255, 240, 190, 0.28) 50%, rgba(255, 220, 120, 0.16) 52%, transparent 70%);
  background-size: 250% 250%;
  animation: shimmer 3.2s linear infinite;
}
@keyframes shimmer {
  0% { background-position: 120% 0; }
  100% { background-position: -130% 0; }
}

.promo-close {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  color: rgba(255, 240, 200, 0.6);
}

.promo-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.85rem;
  color: #1a1200;
  background: linear-gradient(90deg, #FFC400, #FFE082);
  box-shadow: 0 0 18px rgba(255, 196, 0, 0.5);
}

.promo-title {
  margin-top: 14px;
  font-size: 1.55rem;
  font-weight: 900;
  line-height: 1.35;
  background: linear-gradient(90deg, #FFE082, #FFC400, #FFF3D0);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.promo-body {
  margin-top: 10px;
  font-size: 0.95rem;
  line-height: 1.8;
  color: rgba(255, 248, 231, 0.85);
  white-space: pre-line;
}

.promo-feature {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  margin-bottom: 6px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  background: rgba(255, 196, 0, 0.08);
  border: 1px solid rgba(255, 196, 0, 0.18);
}
.promo-feature .q-icon { color: #FFC400; }

.promo-countdown {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.85rem;
  color: #FFE082;
  background: rgba(255, 196, 0, 0.1);
  border: 1px dashed rgba(255, 196, 0, 0.4);
}

.promo-cta {
  padding: 10px 34px;
  font-weight: 800;
  font-size: 1rem;
  color: #1a1200;
  background: linear-gradient(90deg, #FFC400, #FFE082);
  box-shadow: 0 6px 20px rgba(255, 196, 0, 0.4);
}
</style>
